import * as React from 'react';
import type { BookingStep } from '@bondvet/types/booking';
import classnames from 'classnames';
import useWindowSize from 'core/hooks/useWindowSize';
import useBookingStepTracking from 'booking/hooks/useBookingStepTracking'; // GA
import useBrazeSdk from 'analytics/hooks/useBrazeSdk';
import * as styles from './Booking.module.scss';
import BookingProgress from '../BookingProgress';
import BookingHeader from '../BookingHeader';
import useBooking from './useBooking';
import useTrackBookingSessions from '../../hooks/useTrackBookingSessions';

export type BookingProps = {
    step: BookingStep;
};

export default function Booking({
    children,
    step,
}: React.PropsWithChildren<BookingProps>) {
    const root = React.useRef<HTMLDivElement>(null);
    const { height } = useWindowSize();
    const [viewportOffset, setViewportOffset] = React.useState(0);
    const [viewportHeight, setViewportHeight] = React.useState(0);

    useTrackBookingSessions(true);

    useBooking();

    // track booking progress in Google Analytics
    useBookingStepTracking();

    useBrazeSdk();

    // make sure inputs are visible on focus
    React.useEffect(() => {
        const currentRoot = root.current;

        const onFocusIn = (event: FocusEvent) => {
            if (event.target) {
                const target = event.target as Element;

                if (
                    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) &&
                    target.scrollIntoViewIfNeeded !== undefined
                ) {
                    setTimeout(() => {
                        if (target.scrollIntoViewIfNeeded !== undefined) {
                            target.scrollIntoViewIfNeeded(true);
                        }
                    }, 100);
                }
            }
        };

        if (currentRoot) {
            currentRoot.addEventListener('focusin', onFocusIn);
        }

        return () => {
            if (currentRoot) {
                currentRoot.removeEventListener('focusin', onFocusIn);
            }
        };
    }, []);

    React.useEffect(() => {
        const resize = () => {
            if (window.visualViewport) {
                const viewport = window.visualViewport;

                setViewportHeight(viewport.height);

                // prefer the root height, because the body might be _huge_
                const contentHeight =
                    root.current?.getBoundingClientRect().height || height;
                const offset =
                    viewport.height - contentHeight + viewport.offsetTop;

                setViewportOffset(offset * -1);
            }
        };

        const handler = () => {
            resize();
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handler);
            window.visualViewport.addEventListener('scroll', handler);
            resize();
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handler);
                window.visualViewport.removeEventListener('scroll', handler);
            }
        };
    }, [height]);

    return (
        <>
            <div
                className={classnames(styles.root, styles.booted, {
                    [styles.finished]: step === 'finished',
                })}
                ref={root}
                style={
                    {
                        '--viewport-offset': `${viewportOffset}px`,
                        '--viewport-height':
                            viewportHeight === 0
                                ? '100vh'
                                : `${viewportHeight}px`,
                        maxHeight: Math.max(viewportHeight, height),
                    } as React.CSSProperties
                }
            >
                <BookingProgress />
                <BookingHeader />
                <div className={styles.content}>
                    {React.Children.map(children, (child) => {
                        if (React.isValidElement(child)) {
                            return React.cloneElement(
                                child as React.ReactElement<{
                                    className?: string;
                                }>,
                                {
                                    className: classnames(
                                        styles.step,
                                        styles.currentStep,
                                    ),
                                },
                            );
                        }

                        return null;
                    })}
                </div>
            </div>
        </>
    );
}
